// Map zod's flattened-fieldErrors response shape (from REQ-001/REQ-003 server
// errors with code `validation_failed`) onto react-hook-form's setError calls.
//
// The server emits zod's `flattenedFieldErrors` as `error.detail` for any
// 400 validation_failed response — see middleware/error.ts. This helper walks
// that bag and calls setError once per offending field, surfacing the FIRST
// message per field (RHF only renders one error per field anyway).
//
// Lives here rather than co-located with any single form because three
// admin-onboarding surfaces consume it: ClientCreate, ClientEditDrawer,
// InviteContactModal. Generic over the form's field-name union so callers
// keep their type safety.

import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

type FieldErrorBag<TFieldNames extends string> = Partial<Record<TFieldNames, string[]>>;

/**
 * Apply server-side zod field errors to a react-hook-form's setError.
 *
 * @param detail  The `error.detail` payload from an ApiError (REQ-001 shape).
 * @param setError The setError function from a useForm() instance.
 * @returns true if at least one field error was applied (caller can fall
 *          through to a global banner only when this returns false).
 */
export function applyFieldErrors<TForm extends FieldValues>(
  detail: unknown,
  setError: UseFormSetError<TForm>,
): boolean {
  const fieldErrors = (detail as { fieldErrors?: FieldErrorBag<string> } | null)?.fieldErrors;
  if (!fieldErrors) return false;
  let applied = false;
  for (const key of Object.keys(fieldErrors)) {
    const msgs = fieldErrors[key];
    const first = msgs && msgs.length > 0 ? msgs[0] : undefined;
    if (first) {
      setError(key as Path<TForm>, { type: 'server', message: first });
      applied = true;
    }
  }
  return applied;
}
